import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FileExplorer } from '@/components/files/FileExplorer';
import { CodeViewer } from '@/components/files/CodeViewer';
import { EmptyState } from '@/components/ui/EmptyState';
import { FileCode } from 'lucide-react';
import type { Project } from '@/types';

// Mock file structure for demo
const mockFiles = [
  {
    name: 'src',
    path: '/src',
    type: 'directory' as const,
    children: [
      {
        name: 'components',
        path: '/src/components',
        type: 'directory' as const,
        children: [
          { name: 'Button.tsx', path: '/src/components/Button.tsx', type: 'file' as const },
          { name: 'Input.tsx', path: '/src/components/Input.tsx', type: 'file' as const },
          { name: 'Modal.tsx', path: '/src/components/Modal.tsx', type: 'file' as const },
        ],
      },
      {
        name: 'pages',
        path: '/src/pages',
        type: 'directory' as const,
        children: [
          { name: 'Home.tsx', path: '/src/pages/Home.tsx', type: 'file' as const },
          { name: 'About.tsx', path: '/src/pages/About.tsx', type: 'file' as const },
        ],
      },
      { name: 'App.tsx', path: '/src/App.tsx', type: 'file' as const },
      { name: 'main.tsx', path: '/src/main.tsx', type: 'file' as const },
      { name: 'index.css', path: '/src/index.css', type: 'file' as const },
    ],
  },
  { name: 'package.json', path: '/package.json', type: 'file' as const },
  { name: 'tsconfig.json', path: '/tsconfig.json', type: 'file' as const },
  { name: 'vite.config.ts', path: '/vite.config.ts', type: 'file' as const },
  { name: '.env', path: '/.env', type: 'file' as const },
  { name: 'README.md', path: '/README.md', type: 'file' as const },
];

// Mock file content
const mockFileContents: Record<string, string> = {
  '/src/App.tsx': `import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Home } from './pages/Home';
import { About } from './pages/About';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
`,
  '/src/main.tsx': `import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
`,
  '/package.json': `{
  "name": "my-spark-app",
  "private": true,
  "version": "0.0.1",
  "type": "module",
  "scripts": {
    "dev": "vite",
    "build": "tsc && vite build",
    "preview": "vite preview"
  },
  "dependencies": {
    "react": "^18.3.0",
    "react-dom": "^18.3.0",
    "react-router-dom": "^6.20.0"
  },
  "devDependencies": {
    "@types/react": "^18.3.0",
    "@types/react-dom": "^18.3.0",
    "typescript": "^5.3.0",
    "vite": "^5.0.0"
  }
}
`,
  '/README.md': `# My Spark App

This project was generated with Spark AI.

## Getting Started

\`\`\`bash
npm install
npm run dev
\`\`\`

## Features

- React 18 with TypeScript
- Vite for fast development
- React Router for navigation
`,
};

export function ProjectFilesPage() {
  useOutletContext<{ project: Project }>();
  const [selectedFile, setSelectedFile] = useState<string | undefined>();
  const [fileContent, setFileContent] = useState<string>('');

  const handleSelectFile = (path: string) => {
    setSelectedFile(path);
    // In real implementation, fetch file content from API
    setFileContent(mockFileContents[path] || `// File: ${path}\n// Content not available`);
  };

  const handleSaveFile = (content: string) => {
    // In real implementation, save file via API
    console.log('Saving file:', selectedFile, content);
  };

  return (
    <div className="flex h-full">
      {/* File Explorer Sidebar */}
      <div className="w-64 border-r border-gray-200 dark:border-dark-700 flex-shrink-0">
        <FileExplorer
          files={mockFiles}
          selectedFile={selectedFile}
          onSelectFile={handleSelectFile}
          className="h-full"
        />
      </div>

      {/* Code Viewer */}
      <div className="flex-1">
        {selectedFile ? (
          <CodeViewer
            path={selectedFile}
            content={fileContent}
            onSave={handleSaveFile}
            onClose={() => setSelectedFile(undefined)}
            className="h-full"
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <EmptyState
              icon={<FileCode className="w-12 h-12" />}
              title="No file selected"
              description="Select a file from the explorer to view or edit its contents"
            />
          </div>
        )}
      </div>
    </div>
  );
}
